import { useState } from "react"
import { Avatar, Breadcrumb, Button, Dropdown, Tooltip, App as AntdApp } from "antd"
import { LogOut, Menu as MenuIcon, Monitor, Moon, Settings, Sun } from "lucide-react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useLocation, useNavigate } from "react-router-dom"
import type { StaffMe } from "@csbi/contracts"
import { logout } from "../../../features/tiles/api.js"
import { useDashboard } from "../../../features/dashboards/use-dashboards.js"
import { AccountSettings } from "./account-settings.js"
import { DOMAIN_BY_SLUG } from "../../../shared/config/site.js"
import { useTheme, type ThemePreference } from "../../providers/theme-provider.js"

const LABELS: Record<string, string> = {
  domain: "Metric Domains",
  tenants: "Deployments",
  dashboards: "Dashboards",
  catalog: "Metric Dictionary",
  ops: "Data Health",
  coverage: "Coverage Matrix",
  runs: "Collection Runs",
  registry: "Deployment Registry",
}

function crumbsFor(pathname: string, dashboardName?: string): string[] {
  const parts = pathname.split("/").filter(Boolean)
  return parts.map((part, i) => {
    if (parts[i - 1] === "domain") return DOMAIN_BY_SLUG.get(part)?.label ?? part
    if (parts[i - 1] === "dashboards") return dashboardName ?? part
    return LABELS[part] ?? part
  })
}

/** The slug when the current path is a dashboard detail page, otherwise an empty string (which stops `useDashboard` from firing a request) */
function dashboardSlugIn(pathname: string): string {
  const parts = pathname.split("/").filter(Boolean)
  return parts[0] === "dashboards" && parts[1] ? parts[1] : ""
}

export type AppTopbarProps = {
  showMenuButton?: boolean
  onMenuClick?: () => void
  staff?: StaffMe
  dataAsOf?: string
}

const THEME_ITEMS: { key: ThemePreference; label: string; icon: typeof Sun }[] = [
  { key: "light", label: "Light", icon: Sun },
  { key: "dark", label: "Dark", icon: Moon },
  { key: "system", label: "Match system", icon: Monitor },
]

export function AppTopbar({ showMenuButton, onMenuClick, staff, dataAsOf }: AppTopbarProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { message } = AntdApp.useApp()
  const [settingsOpen, setSettingsOpen] = useState(false)

  const signOut = useMutation({
    mutationFn: logout,
    onSettled: () => {
      qc.clear()
      void navigate("/login", { replace: true })
    },
    onError: () => void message.warning("The sign-out request did not reach the server; your local session was cleared.", 5),
  })
  const { preference, mode, setPreference } = useTheme()
  const { data: dashboard } = useDashboard(dashboardSlugIn(location.pathname))
  const crumbs = crumbsFor(location.pathname, dashboard?.name)
  const ThemeIcon = preference === "system" ? Monitor : mode === "dark" ? Moon : Sun

  return (
    /**
     * ⚠ **`--rd-nav-height` is the height of the whole block, stripe included** — not of the row.
     */
    <div
      style={{
        background: "var(--csbi-surface)",
        height: "var(--rd-nav-height, 64px)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          // Takes whatever the stripe leaves. Not a fixed number, so changing the stripe's weight cannot desync the two columns
          flex: 1,
          minHeight: 0,
          padding: "0 24px",
        }}
      >
        {showMenuButton && (
          <Button type="text" size="small" aria-label="Open menu" icon={<MenuIcon size={16} />} onClick={onMenuClick} />
        )}
        <Breadcrumb items={crumbs.map((c) => ({ title: c }))} />

        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
          {dataAsOf && (
            <Tooltip title="When this screen's data was generated. Every tile here comes from the same query, so there is only one such moment.">
              <span style={{ fontSize: 12, color: "var(--csbi-ink-muted)" }}>Data as of {dataAsOf}</span>
            </Tooltip>
          )}
          <Dropdown
            menu={{
              selectedKeys: [preference],
              items: THEME_ITEMS.map(({ key, label, icon: Icon }) => ({
                key,
                label,
                icon: <Icon size={14} />,
                onClick: () => setPreference(key),
              })),
            }}
          >
            <Button type="text" size="small" aria-label="Switch theme" icon={<ThemeIcon size={15} />} />
          </Dropdown>
          {staff ? (
            <>
              <Dropdown
                trigger={["click"]}
                menu={{
                  items: [
                    {
                      key: "who",
                      label: <span style={{ fontSize: 12, color: "var(--csbi-ink-muted)" }}>{staff.account}</span>,
                      disabled: true,
                    },
                    { type: "divider" },
                    {
                      key: "settings",
                      label: "Settings",
                      icon: <Settings size={14} />,
                      onClick: () => setSettingsOpen(true),
                    },
                    {
                      key: "logout",
                      label: "Sign out",
                      icon: <LogOut size={14} />,
                      onClick: () => signOut.mutate(),
                    },
                  ],
                }}
              >
                <Button type="text" size="small" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Avatar size={22} style={{ background: "var(--csbi-series-1)", fontSize: 11 }}>
                    {initialsOf(staff.username)}
                  </Avatar>
                  <span style={{ fontSize: 13 }}>{staff.username}</span>
                </Button>
              </Dropdown>
              <AccountSettings open={settingsOpen} staff={staff} onClose={() => setSettingsOpen(false)} />
            </>
          ) : (
            <span style={{ fontSize: 13, color: "var(--csbi-ink-muted)" }}>Not signed in</span>
          )}
        </div>
      </div>

      <div className="rd-stripe" />
    </div>
  )
}

function initialsOf(username: string): string {
  return (username.trim()[0] ?? "?").toUpperCase()
}
